<template>
  <div :class="{ 'rounded-te': ! hasTitle }" class="bg-lightgrey rounded-be d-flex flex-column pb-2">
    <template v-if="planningDrawerTabIndex != '' && ! hasTitle">
      <v-btn-icon size="small" class="mx-auto" @click="close"><v-icon size="large">far fa-times</v-icon></v-btn-icon>
    </template>
    <v-slide-group direction="vertical" prev-icon="far fa-angle-up" next-icon="far fa-angle-down" class="fill-height">
      <div v-if="el" :class="darkMode ? 'filter-secondary' : 'bg-secondary'">
        <v-btn-icon :active="planningDrawerTabIndex == 'element'" class="rounded-0"
                    @click="elementClick(el)">
          <v-icon>far fa-file-edit</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t('PLANNINGDRAWER.CONTENT') }}</v-tooltip>
        </v-btn-icon>
        <v-btn-icon :active="planningDrawerTabIndex == 'color-picker'" class="rounded-0"
                    @click="selectColor(el)">
          <v-icon>far fa-tint</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t(el.isType('macro', 'task') ? 'PLANNING.SELECT_ICON_AND_COLOR' : 'PLANNING.SELECT_ICON') }}</v-tooltip>
        </v-btn-icon>
        <v-btn-icon v-if="! el.isType('macro')" :active="planningDrawerTabIndex == 'dependencies'" :disabled="! el.hasDates() || dashboard" class="rounded-0"
                    @click="openDependencies(el)">
          <v-icon>far fa-link</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t('PLANNING.DEPENDENCIES') }}</v-tooltip>
        </v-btn-icon>
        <v-btn-icon v-if="el.isType('milestone') && el.getMeetingId()" :active="planningDrawerTabIndex == 'meeting'" class="rounded-0"
                    @click="openMeeting(el)">
          <v-icon>far fa-users</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t('MEETING.BUBBLE_MEETING') }}</v-tooltip>
        </v-btn-icon>
      </div>

      <v-spacer></v-spacer>
      <v-btn-icon v-if="el && ! showBottomButtonsWithElOpened" class="rounded-b-0" style="height: auto" @click="showBottomButtonsWithElOpened = true">
        <v-icon class="py-4">far fa-angle-up</v-icon>
      </v-btn-icon>
      <div v-if="! el || showBottomButtonsWithElOpened" :class="darkMode ? 'filter-secondary' : 'bg-secondary'" class="mt-4">
        <template v-if="canModifyPlanning">
          <v-btn-icon :active="planningDrawerTabIndex == 'selection'" class="rounded-0"
                      @click="open('selection')">
            <v-icon :disabled="selectionStore.selectionCount <= 1">far fa-square-dashed-circle-plus</v-icon>
            <premium-section feature="selection">
              <span v-show="selectionStore.selectionCount > 1" class="planningdrawer-badge bg-successgreen">{{ selectionStore.selectionCount }}</span>
              <template #noaccess><span style="font-size: 1.2"><premium-section icon feature="selection" style="position: absolute; top: 4px; right: 4px;"></premium-section></span></template>
            </premium-section>
            <v-tooltip location="left" activator="parent">{{ $t('PLANNINGDRAWER.OPEN_SELECTION') }}</v-tooltip>
          </v-btn-icon>
          <v-btn-icon v-if="! hideExchangeCenter" :active="planningDrawerTabIndex == 'exchanges'" class="rounded-0"
                      @click="open('exchanges')">
            <v-icon>far fa-comments</v-icon>
            <span v-if="unread.exchanges" class="planningdrawer-badge bg-errorred">{{ unread.exchanges }}</span>
            <v-tooltip location="left" activator="parent">{{ $t('PLANNINGDRAWER.OPEN_EXCHANGECENTER') }}</v-tooltip>
          </v-btn-icon>
          <v-btn-icon :active="planningDrawerTabIndex == 'actions'" class="rounded-0"
                      @click="open('actions')">
            <v-icon>far fa-check-square</v-icon>
            <div v-if="countLateActions" class="planningdrawer-badge bg-errorred">{{ countLateActions }}</div>
            <v-tooltip location="left" activator="parent">{{ $t('PLANNINGDRAWER.OPEN_ACTIONS') }}</v-tooltip>
          </v-btn-icon>
        </template>
        <v-btn-icon v-if="canModifyPlanning || dashboard" :active="planningDrawerTabIndex == 'config'" class="rounded-0 tour-timelineconfig"
                    @click="openConfig">
          <v-icon>far fa-cog</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t('PLANNING.PLANNING_CONFIG') }}</v-tooltip>
        </v-btn-icon>
      </div>
      <div>
        <v-btn-icon class="rounded-0"
                    @click="openZohoChat">
          <v-icon>far fa-question</v-icon>
          <v-tooltip location="left" activator="parent">{{ $t('HELP.NEED_HELP') }}</v-tooltip>
        </v-btn-icon>
        <template v-if="logoutButton">
          <!-- MICROSOFT TEAMS WITH NO NAVBAR -->
          <v-divider></v-divider>
          <v-btn-icon class="rounded-0" @click="logout">
            <v-icon>far fa-sign-out-alt</v-icon>
            <v-tooltip location="left" activator="parent">{{ $t('ACCOUNT.LOG_OUT') }}</v-tooltip>
          </v-btn-icon>
        </template>
      </div>
    </v-slide-group>
  </div>
</template>

<style lang="scss">
  #planningdrawer .planningdrawer-badge {
    position: absolute;
    top: 0;
    right: 0;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 75%;
    overflow: hidden;
  }
</style>

<script>
  import { mapState, mapActions } from 'pinia';
  import useUiPlanningStore from '@/stores/ui/planning';
  import useSelectionStore from '@/stores/selection';
  import useUsersStore from '@/stores/users';
  import useNotificationsStore from '@/stores/notifications';
  import darkModeMixin from '@/components/Manage/ManageProfile/darkModeMixin';
  import clientConfig from '@/client_customs/config';

  export default {
    props: {
      planning: { type: Object, required: true },
      events: { type: Object, default: () => ({}) },
      canModifyPlanning: { type: Boolean, default: false },
      hasTitle: { type: Boolean, default: false },
    },
    emits: ['open', 'close'],
    mixins: [darkModeMixin],
    data() {
      return {
        showBottomButtonsWithElOpened: false,
        hideExchangeCenter: clientConfig.planningDrawer?.hideExchangeCenter || false,
        logoutButton: clientConfig.planningDrawer?.logoutButton || false,
        uiPlanningStore: useUiPlanningStore(),
        selectionStore: useSelectionStore(),
        usersStore: useUsersStore(),
        notificationsStore: useNotificationsStore(),
      };
    },
    computed: {
      el() {
        if (this.planningDrawerTabIndex == 'element') return this.openedElement;
        if (this.planningDrawerTabIndex == 'color-picker') return this.openedColorPickerElement;
        if (this.planningDrawerTabIndex == 'dependencies') return this.openedDependenciesElement;
        if (this.planningDrawerTabIndex == 'meeting') return this.openedMeetingElement;
        return null;
      },
      notifications() {
        return this.notificationsStore.all;
      },
      countLateActions() {
        const me = this.usersStore.user;
        return this.planning.elements.reduce((count, el) => {
          if (! el.getChecklist()) return count;
          return count + el.getChecklist().filter(item => ! item.checked && item.user_id == me.id && moment(item.duedate || el.getEndTime()).isBefore()).length;
        }, 0);
      },
      unreadNotifications() {
        return this.notifications.filter(notif => notif.section_type == 'planning' && notif.section_id == this.planning.id && ! notif.seen_at);
      },
      unread() {
        return {
          exchanges: this.unreadNotifications.filter(notif => notif.target_type == 'exchangeCenter').length,
        };
      },
      ...mapState(useUiPlanningStore, ['dashboard', 'planningDrawerTabIndex', 'openedElement', 'openedColorPickerElement', 'openedDependenciesElement', 'openedMeetingElement']),
    },
    watch: {
      el() {
        this.showBottomButtonsWithElOpened = false;
      },
    },
    created() {
      window.zoho_donotshow = true; // block initial show
      if (window.$zoho?.salesiq?.floatbutton) window.$zoho.salesiq.floatbutton.visible('hide');
    },
    unmounted() {
      if (window.$zoho?.salesiq?.floatbutton) window.$zoho.salesiq.floatbutton.visible('show');
      if (window.$ZSIQWidgetUI) window.$ZSIQWidgetUI.minimizeSticker();
    },
    methods: {
      elementClick(el) {
        if (this.events.elementClick) {
          this.events.elementClick(el);
          return;
        }
        this.uiPlanningStore.openElementDetails(el);
      },
      selectColor(el) {
        if (this.events.selectColor) {
          this.events.selectColor(el);
          return;
        }
        this.uiPlanningStore.openColorPicker(el);
      },
      openZohoChat() {
        if (! window.$zoho) return;
        window.$zoho.salesiq.floatwindow.visible('show');
      },
      logout() {
        window.apiSrv.logout().then(() => {
          this.usersStore.reset();
          window.location.reload();
        }).catch(() => {
          this.usersStore.reset();
          window.location.reload();
        });
      },
      open(tab) {
        this.$emit('open', tab);
      },
      close() {
        this.$emit('close');
      },
      ...mapActions(useUiPlanningStore, ['openConfig', 'openDependencies', 'openMeeting']),
    },
  };
</script>
